import { useEffect } from "react";
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
  type QueryClient,
} from "@tanstack/react-query";
import { api } from "../firebase/api-client";
import type { OrderStatus } from "../constants";
import type { MonthlyDashboardResponse } from "@/types";

/**
 * Query Keys - Centralisés pour éviter les typos
 */
export const queryKeys = {
  // Stores
  stores: ["stores"] as const,
  store: (id: string) => ["stores", id] as const,

  // Orders
  orders: (filters?: { storeId?: string; status?: OrderStatus }) =>
    ["orders", filters] as const,
  order: (id: string) => ["orders", id] as const,

  // Users
  collaborateurs: ["collaborateurs"] as const,
  fournisseurs: ["fournisseurs"] as const,
  users: ["users"] as const,

  // Products
  products: (filters?: { limit?: number }) => ["products", filters] as const,
  categories: (filters?: { isActive?: boolean }) =>
    ["categories", filters] as const,

  // Stats
  adminStats: ["admin", "stats"] as const,
  monthlyDashboard: (y: number, m: number, storeId?: string) =>
    ["admin", "dashboard", "monthly", y, m, storeId ?? "ALL"] as const,
  fournisseurStats: (fournisseurId: string) =>
    ["fournisseur", fournisseurId, "stats"] as const,

  // Baskets
  baskets: (filters?: { fournisseurId?: string; orderId?: string }) =>
    ["baskets", filters] as const,
  basket: (id: string) => ["baskets", id] as const,
};

/**
 * ============================================
 * STORES QUERIES
 * ============================================
 */

export function useStores() {
  return useQuery({
    queryKey: queryKeys.stores,
    queryFn: () => api.stores.getAll(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (anciennement cacheTime)
  });
}

export function useStore(id: string) {
  return useQuery({
    queryKey: queryKeys.store(id),
    queryFn: () => api.stores.getById(id),
    staleTime: 5 * 60 * 1000,
    enabled: !!id,
  });
}

/**
 * ============================================
 * LE COMPTEUR DE CHANGEMENTS DES COMMANDES (06/10/2026)
 * ============================================
 *
 * Avant : les listes de commandes, de paniers et le tableau de bord se
 * rechargeaient ENTIEREMENT toutes les 30 ou 60 secondes, meme quand rien
 * n'avait bouge. Une liste de commandes relit ~250 documents ; le tableau
 * de bord, ~1 000.
 *
 * Maintenant : on lit toutes les 15 s un compteur (1 document) que le
 * serveur augmente a chaque changement de commande, d'article ou de panier.
 * La liste ne se recharge QUE s'il a bouge — et jamais plus souvent
 * qu'avant (30 s pour les listes, 60 s pour le tableau de bord). Un filet :
 * rechargement complet toutes les 5 minutes quoi qu'il arrive.
 *
 * Si le compteur ne repond pas (serveur pas encore deploye, panne), les
 * ecrans reviennent tout seuls a l'ancien rythme. Les ALARMES de commandes
 * et de paniers ne passent pas par ici : elles sont inchangees.
 */
/**
 * CE QUE CES TROIS REGLAGES COUTENT
 *
 * Le compteur dit « quelque chose a change » pour une lecture de rien du
 * tout. Mais le rechargement qu'il declenche, lui, relit la liste entiere
 * sur CHAQUE poste ouvert. Avec quatre ou cinq postes en journee et un
 * compteur qui bouge plus de mille fois par jour (chaque commande, chaque
 * article, chaque panier l'incremente), c'est ce rechargement qui faisait
 * 1,2 million de lectures Firestore par jour — l'essentiel de la facture
 * Google, releve le 08/10/2026.
 *
 * L'ecart minimum est le garde-fou : meme si le compteur s'affole, un ecran
 * ne se recharge pas plus souvent que ca. Il valait 2 minutes pour tout le
 * monde le 08/10 ; depuis le 09/10 il depend de la presence devant l'ecran
 * — voir le bloc suivant. Le FILET_COMPLET de 5 min, lui, n'a pas bouge :
 * rien ne peut etre rate durablement.
 */
const RELEVE_VERSION = 20 * 1000;
const FILET_COMPLET = 5 * 60 * 1000;

/* ==========================================================================
 *  L'ECART MINIMUM DEPEND DE LA PRESENCE (09/10/2026)
 * ==========================================================================
 *
 *  Le 08/10, ECART_MINI valait 2 minutes pour tout le monde. L'economie
 *  etait reelle, mais elle se payait sur le dos du support : une commande
 *  pouvait mettre deux minutes a apparaitre, contre trente secondes avant.
 *  Sur une operation de livraison, deux minutes d'ignorance comptent.
 *
 *  Or ce ne sont pas les ecrans qu'on REGARDE qui coutent cher. React Query
 *  arrete deja de sonder quand l'onglet est cache. Ce qui reste, et que rien
 *  n'arretait, c'est l'ecran VISIBLE SANS PERSONNE DEVANT : un poste laisse
 *  ouvert sur un coin de bureau, qui rechargeait la liste entiere toute la
 *  journee pour personne.
 *
 *  On distingue donc les deux, et le critere n'est pas la visibilite mais
 *  le dernier geste humain :
 *
 *    quelqu'un travaille        30 s   la reactivite d'avant, retrouvee
 *    plus un geste depuis 2 min  5 min  personne ne regarde, on se calme
 *
 *  Et au retour — un clic, une touche, l'onglet qu'on revient voir — les
 *  listes se rechargent IMMEDIATEMENT, sans attendre le releve suivant.
 *  Revenir devant son ecran et y lire des donnees vieilles de cinq minutes
 *  serait pire que tout ce qu'on cherche a economiser.
 * ======================================================================== */

const ECART_PRESENT = 30 * 1000;
const ECART_ABSENT = 5 * 60 * 1000;
const DELAI_INACTIVITE = 2 * 60 * 1000;

let dernierGeste = Date.now();
let absent = false;

/** Les familles d'ecrans qui veulent etre prevenues d'un retour. */
const auRetour = new Set<() => void>();

function present(): boolean {
  // Un onglet cache n'a personne devant, quelle que soit l'heure du
  // dernier geste : on ne reveille pas un ecran que personne ne voit.
  if (typeof document !== "undefined" && document.visibilityState === "hidden") {
    return false;
  }
  return Date.now() - dernierGeste < DELAI_INACTIVITE;
}

function ecartActuel(): number {
  return present() ? ECART_PRESENT : ECART_ABSENT;
}

if (typeof window !== "undefined") {
  const noter = () => {
    dernierGeste = Date.now();
    if (absent) {
      absent = false;
      // Le retour : on ne fait pas attendre la personne.
      auRetour.forEach((f) => {
        try {
          f();
        } catch {
          /* Un ecran qui refuse de se rafraichir n'empeche pas les autres. */
        }
      });
    }
  };

  // Un simple horodatage : assez leger pour etre pose sur mousemove.
  for (const ev of [
    "pointerdown",
    "keydown",
    "wheel",
    "touchstart",
    "mousemove",
  ]) {
    window.addEventListener(ev, noter, { passive: true });
  }

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") noter();
  });

  // Marque l'absence des que le delai est franchi, pour que le retour
  // suivant soit reconnu comme tel et declenche le rechargement.
  window.setInterval(() => {
    if (!absent && Date.now() - dernierGeste >= DELAI_INACTIVITE) absent = true;
  }, 30 * 1000);
}

const versionVue: Record<string, number> = {};
const dernierRechargement: Record<string, number> = {};

function appliquerVersion(
  qc: QueryClient,
  n: number,
  famille: string,
  queryKey: readonly unknown[],
) {
  const ecartMin = ecartActuel();
  const maintenant = Date.now();
  if (versionVue[famille] === undefined) {
    versionVue[famille] = n;
    dernierRechargement[famille] = maintenant;
    return;
  }
  if (n === versionVue[famille]) return;
  // Change, mais rechargee il y a peu : on attend le releve suivant, qui
  // verra toujours la difference. Jamais plus souvent qu'avant.
  if (maintenant - (dernierRechargement[famille] || 0) < ecartMin) return;
  versionVue[famille] = n;
  dernierRechargement[famille] = maintenant;
  void qc.invalidateQueries({ queryKey });
}

/**
 * Surveille le compteur pour une famille d'ecrans. Rend `true` tant que le
 * compteur repond : l'appelant peut alors espacer son propre rechargement.
 */
function useVersionCommandes(
  actif: boolean,
  famille: string,
  queryKey: readonly unknown[],
): boolean {
  const qc = useQueryClient();
  const { data, dataUpdatedAt, isSuccess, isError } = useQuery({
    queryKey: ["versionCommandes"],
    queryFn: () => api.orders.version(),
    enabled: actif,
    refetchInterval: RELEVE_VERSION,
    staleTime: 0,
    gcTime: 60 * 1000,
    retry: false,
  });

  useEffect(() => {
    const n = data?.n;
    if (typeof n !== "number") return;
    appliquerVersion(qc, n, famille, queryKey);
    // queryKey est une constante par famille : seul un nouveau releve compte.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpdatedAt, data?.n, qc, famille]);

  // Au retour de la personne, cette famille se recharge tout de suite.
  useEffect(() => {
    if (!actif) return;
    const rafraichir = () => {
      dernierRechargement[famille] = Date.now();
      void qc.invalidateQueries({ queryKey });
    };
    auRetour.add(rafraichir);
    return () => {
      auRetour.delete(rafraichir);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actif, famille, qc]);

  return actif && isSuccess && !isError && typeof data?.n === "number";
}

/**
 * ============================================
 * ORDERS QUERIES
 * ============================================
 */

export function useOrders(filters?: {
  storeId?: string;
  status?: OrderStatus;
  limit?: number;
  page?: number;
  dateFilter?: string;
  phone?: string;
  code?: string;
  source?: string;
}) {
  const compteurOk = useVersionCommandes(true, "orders", ["orders"]);
  return useQuery({
    queryKey: queryKeys.orders(filters),
    queryFn: async () => {
      const result = await api.orders.getAll(filters);
      return result; // { orders: [], pagination: {} }
    },
    staleTime: 30 * 1000,
    gcTime: 2 * 60 * 1000,
    // Rechargement guide par le compteur ; 30 s comme avant s'il ne repond pas.
    refetchInterval: compteurOk ? FILET_COMPLET : 30 * 1000,
  });
}

export function useOrderStats(
  filters?: {
    storeId?: string;
    dateFilter?: string;
    source?: string;
  },
  options?: {
    /** Quinzaine cloturee (02/11/2026) : les chiffres ne bougent plus, inutile
     *  de les redemander toutes les minutes. */
    figee?: boolean;
  },
) {
  const figee = !!options?.figee;
  return useQuery({
    queryKey: ["orderStats", filters],
    queryFn: async () => {
      const result = await api.orders.getStats(filters);
      return result;
    },
    staleTime: figee ? 10 * 60 * 1000 : 60 * 1000, // 🔥 Cache 1 minute (stats changent moins souvent)
    gcTime: 5 * 60 * 1000,
    refetchInterval: figee ? false : 60 * 1000, // Refetch toutes les 60 secondes
  });
}

export function useOrder(id: string) {
  return useQuery({
    queryKey: queryKeys.order(id),
    queryFn: () => api.orders.getById(id),
    staleTime: 30 * 1000,
    enabled: !!id,
  });
}

/**
 * ============================================
 * USERS QUERIES
 * ============================================
 */

export function useCollaborateurs() {
  return useQuery({
    queryKey: queryKeys.collaborateurs,
    queryFn: () => api.collaborateurs.getAll(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useFournisseurs() {
  return useQuery({
    queryKey: queryKeys.fournisseurs,
    queryFn: () => api.fournisseurs.getAll(),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * ============================================
 * PRODUCTS QUERIES
 * ============================================
 */

export function useProducts(filters?: { limit?: number }) {
  return useQuery({
    queryKey: queryKeys.products(filters),
    queryFn: () => api.products.getAll(filters),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCategories(filters?: { isActive?: boolean }) {
  return useQuery({
    queryKey: queryKeys.categories(filters),
    queryFn: () => api.categories.getAll(filters),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * ============================================
 * ADMIN STATS QUERY
 * ============================================
 */

interface AdminStats {
  stores: { total: number; active: number };
  collaborateurs: number;
  fournisseurs: number;
  categories: number;
  products: number;
  orders: {
    total: number;
    pending: number;
    inPreparation: number;
    delivered: number;
  };
}

export function useMonthlyDashboard(filters: {
  year: number;
  month: number;
  storeId?: string;
}) {
  return useQuery({
    queryKey: queryKeys.monthlyDashboard(
      filters.year,
      filters.month,
      filters.storeId,
    ),
    queryFn: (): Promise<MonthlyDashboardResponse> =>
      api.dashboard.getMonthly({
        year: filters.year,
        month: filters.month,
        storeId: filters.storeId,
      }),
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

export function useAdminStats() {
  const compteurOk = useVersionCommandes(true, "adminStats", queryKeys.adminStats);
  return useQuery({
    queryKey: queryKeys.adminStats,
    queryFn: async (): Promise<AdminStats> => {
      const [
        stores,
        collaborateurs,
        fournisseurs,
        categories,
        productsData,
        ordersResponse,
      ] = await Promise.all([
        api.stores.getAll().catch(() => []),
        api.collaborateurs.getAll().catch(() => []),
        api.fournisseurs.getAll().catch(() => []),
        api.categories.getAll({ isActive: true }).catch(() => []),
        api.products.getAll({ limit: 1 }).catch(() => ({ total: 0 })),
        api.orders
          .getAll({ limit: 100 })
          .catch(() => ({ orders: [], pagination: null })),
      ]);

      const orders = (ordersResponse as any).orders || [];
      const activeStores = stores.filter((s: any) => s.isActive);
      const pendingOrders = orders.filter((o: any) => o.status === "PENDING");
      const inPrepOrders = orders.filter(
        (o: any) =>
          o.status === "IN_PREPARATION" ||
          o.status === "GLOVO_ACCEPTED" ||
          o.status === "GLOVO_READY",
      );
      const deliveredOrders = orders.filter(
        (o: any) => o.status === "GLOVO_DELIVERED" || o.status === "DELIVERED",
      );

      return {
        stores: {
          total: stores.length,
          active: activeStores.length,
        },
        collaborateurs: collaborateurs.length,
        fournisseurs: fournisseurs.length,
        categories: categories.length,
        products: (productsData as any).total || 0,
        orders: {
          total: orders.length,
          pending: pendingOrders.length,
          inPreparation: inPrepOrders.length,
          delivered: deliveredOrders.length,
        },
      };
    },
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
    // Rechargement guide par le compteur ; 60 s comme avant s'il ne repond pas.
    refetchInterval: compteurOk ? FILET_COMPLET : 60 * 1000,
  });
}

/**
 * ============================================
 * BASKETS QUERIES
 * ============================================
 */

export function useBaskets(filters?: {
  fournisseurId?: string;
  orderId?: string;
  status?: string;
  limit?: number;
  page?: number;
}) {
  const actif = !!filters?.fournisseurId || !!filters?.orderId;
  const compteurOk = useVersionCommandes(actif, "baskets", ["baskets"]);
  return useQuery({
    queryKey: queryKeys.baskets(filters),
    queryFn: async () => {
      const result = await api.baskets.getAll(filters);
      return result; // { baskets: [], pagination: {} }
    },
    staleTime: 30 * 1000, // 30 secondes (baskets changent souvent)
    gcTime: 2 * 60 * 1000, // 2 minutes
    enabled: actif,
    // Rechargement guide par le compteur ; 30 s comme avant s'il ne repond pas.
    refetchInterval: compteurOk ? FILET_COMPLET : 30 * 1000,
  });
}

export function useBasket(id: string) {
  return useQuery({
    queryKey: queryKeys.basket(id),
    queryFn: () => api.baskets.getById(id),
    staleTime: 30 * 1000,
    enabled: !!id,
  });
}

/**
 * ============================================
 * MUTATIONS
 * ============================================
 */

/**
 * Mutation pour créer un store
 */
export function useCreateStore() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => api.stores.create(data),
    onSuccess: () => {
      // Invalider le cache des stores pour refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.stores });
      queryClient.invalidateQueries({ queryKey: queryKeys.adminStats });
    },
  });
}

/**
 * Mutation pour mettre à jour un store
 */
export function useUpdateStore() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      api.stores.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.stores });
      queryClient.invalidateQueries({
        queryKey: queryKeys.store(variables.id),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.adminStats });
    },
  });
}

/**
 * Mutation pour supprimer un store
 */
export function useDeleteStore() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.stores.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.stores });
      queryClient.invalidateQueries({ queryKey: queryKeys.adminStats });
    },
  });
}

/**
 * Mutation pour synchroniser les produits Glovo
 */
export function useSyncStoreProducts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (storeId: string) => api.stores.syncProducts(storeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.products() });
      queryClient.invalidateQueries({ queryKey: queryKeys.categories() });
      queryClient.invalidateQueries({ queryKey: queryKeys.adminStats });
    },
  });
}

/**
 * Mutation pour supprimer les produits et catégories
 */
export function useClearProductsAndCategories() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.stores.clearProductsAndCategories(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.products() });
      queryClient.invalidateQueries({ queryKey: queryKeys.categories() });
      queryClient.invalidateQueries({ queryKey: queryKeys.adminStats });
    },
  });
}

/**
 * Mutation pour supprimer les commandes
 */
export function useClearOrders() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.admin.clearOrders(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.orders() });
      queryClient.invalidateQueries({ queryKey: queryKeys.adminStats });
    },
  });
}

/**
 * Mutation pour marquer un panier comme prêt
 */
/**
 * « Je m'y mets. »
 *
 * Calque sur useMarkBasketReady : memes invalidations, pour que l'ecran du
 * fournisseur ET celui du responsable voient le changement.
 */
export function useCommencerBasket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (basketId: string) => api.baskets.commencer(basketId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["baskets"] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
  });
}

export function useMarkBasketReady() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (basketId: string) => api.baskets.markReady(basketId),
    onSuccess: () => {
      // ✅ Invalider TOUTES les queries baskets (avec ou sans filtres)
      queryClient.invalidateQueries({ queryKey: ["baskets"] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
  });
}

/**
 * Mutation pour récupérer un panier
 */
export function usePickupBasket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (basketId: string) => api.baskets.pickup(basketId),
    onSuccess: () => {
      // ✅ Invalider TOUTES les queries baskets et orders
      queryClient.invalidateQueries({ queryKey: ["baskets"] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
  });
}
